import React, { createContext, useContext, useState, useEffect } from 'react'
import { getThemeColors, themeList } from '@/utils/colors'

const ThemeContext = createContext()

export const useTheme = () => {
  const context = useContext(ThemeContext)
  if (!context) {
    throw new Error('useTheme must be used within a ThemeProvider')
  }
  return context
}

// تطبيق الثيم فوراً قبل الرندر لتجنب الوميض
const getInitialDarkMode = () => {
  if (typeof window !== 'undefined') {
    const savedTheme = localStorage.getItem('theme')
    if (savedTheme) {
      return savedTheme === 'dark'
    }
    // Light Mode is the default
    return false
  }
  return false
}

// الحصول على ثيم الألوان الأولي
// Always return 'green' as it's the only available theme
const getInitialColorTheme = () => {
  // Always use green theme - no other themes available
  return 'green'
}

// تطبيق الثيم فوراً على المستند
const applyTheme = (isDark) => {
  if (typeof window !== 'undefined') {
    if (isDark) {
      document.documentElement.classList.add('dark')
      localStorage.setItem('theme', 'dark')
    } else {
      document.documentElement.classList.remove('dark')
      localStorage.setItem('theme', 'light')
    }
  }
}

export const ThemeProvider = ({ children }) => {
  // تحديد الثيم الأولي (dark/light mode)
  const [isDarkMode, setIsDarkMode] = useState(() => {
    const initialTheme = getInitialDarkMode()
    applyTheme(initialTheme)
    return initialTheme
  })

  // تحديد ثيم الألوان الأولي
  const [colorTheme, setColorTheme] = useState(() => getInitialColorTheme())

  // الحصول على ألوان الثيم الحالي
  const colors = React.useMemo(() => getThemeColors(colorTheme), [colorTheme])

  // تطبيق CSS variables عند تغيير الثيم - تطبيق فوري قبل الرندر
  const applyCSSVariables = React.useCallback((themeName) => {
    if (typeof document !== 'undefined') {
      const root = document.documentElement
      const theme = getThemeColors(themeName)
      
      // Set CSS variables for theme colors
      root.style.setProperty('--primary-light', theme.primary.light)
      root.style.setProperty('--primary-main', theme.primary.main)
      root.style.setProperty('--primary-dark', theme.primary.dark)
      root.style.setProperty('--primary-bright', theme.primary.bright)
      
      // Set RGB values for CSS usage with opacity
      const lightRgb = theme.primary.light.match(/\d+/g)
      const mainRgb = theme.primary.main.match(/\d+/g)
      const brightRgb = theme.primary.bright.match(/\d+/g)
      
      if (lightRgb) {
        root.style.setProperty('--primary-light-rgb', lightRgb.join(', '))
        root.style.setProperty('--primary-light-alpha', `rgba(${lightRgb.join(', ')}, 0.4)`)
      }
      if (mainRgb) {
        root.style.setProperty('--primary-main-rgb', mainRgb.join(', '))
        root.style.setProperty('--primary-main-alpha', `rgba(${mainRgb.join(', ')}, 0.3)`)
      }
      if (brightRgb) {
        root.style.setProperty('--primary-bright-rgb', brightRgb.join(', '))
        root.style.setProperty('--primary-bright-alpha', `rgba(${brightRgb.join(', ')}, 0.5)`)
      }
      
      // Set gradient variables
      root.style.setProperty('--gradient-primary', theme.gradients.primary)
      root.style.setProperty('--gradient-button', theme.gradients.button)
      root.style.setProperty('--gradient-text', theme.gradients.text)
      root.style.setProperty('--gradient-sidebar-active', theme.gradients.sidebarActive)
      
      // Set shadow variables
      root.style.setProperty('--shadow-glow', theme.shadows.glow)
      root.style.setProperty('--shadow-card', theme.shadows.card)
      root.style.setProperty('--shadow-button', theme.shadows.button)
      
      // Store theme in a data attribute for CSS selectors if needed
      root.setAttribute('data-theme', themeName)
      
      console.log(`🎨 Theme CSS variables applied: ${themeName}`)
    }
  }, [])

  // تطبيق CSS variables عند تحميل الصفحة وعند تغيير الثيم
  useEffect(() => {
    applyCSSVariables(colorTheme)
  }, [colorTheme, applyCSSVariables])

  // تطبيق CSS variables فوراً عند التحميل الأولي (قبل أول رندر)
  React.useLayoutEffect(() => {
    const initialTheme = getInitialColorTheme()
    applyCSSVariables(initialTheme)
  }, [])

  // التحقق من التفضيل المحفوظ عند التحميل
  useEffect(() => {
    const savedTheme = localStorage.getItem('theme')
    
    // Default to light when no preference is saved
    let shouldBeDark = false
    
    if (savedTheme) {
      shouldBeDark = savedTheme === 'dark'
    }
    
    // Always use green theme - ignore saved theme
    setColorTheme('green')
    
    if (shouldBeDark !== isDarkMode) {
      setIsDarkMode(shouldBeDark)
      applyTheme(shouldBeDark)
    }
  }, [])

  // تطبيق الثيم عند تغيير الحالة
  useEffect(() => {
    applyTheme(isDarkMode)
  }, [isDarkMode])

  const toggleTheme = () => {
    const newTheme = !isDarkMode
    setIsDarkMode(newTheme)
    applyTheme(newTheme)
    
    console.log(`🎨 تم تغيير الثيم إلى: ${newTheme ? 'المظلم' : 'الفاتح'}`)
    console.log(`💾 تم حفظ التفضيل في localStorage: ${newTheme ? 'dark' : 'light'}`)
  }

  const setColorThemeName = (themeName) => {
    // Only green theme is available - ignore other theme requests
    if (themeName === 'green') {
      setColorTheme('green')
      localStorage.setItem('colorTheme', 'green')
      console.log(`🎨 Theme set to: green`)
    } else {
      console.warn(`⚠️ Only green theme is available. Requested theme "${themeName}" ignored.`)
    }
  }

  const value = {
    isDarkMode,
    toggleTheme,
    theme: isDarkMode ? 'dark' : 'light',
    colorTheme,
    setColorTheme: setColorThemeName,
    colors,
    availableColorThemes: themeList,
  }

  return (
    <ThemeContext.Provider value={value}>
      {children}
    </ThemeContext.Provider>
  )
}

export default ThemeContext